import React from 'react';
import BackToHome from './BackToHome';
import './LegalPages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="site-page-topbar legal-page-topbar">
          <BackToHome />
        </div>

        <div className="legal-content">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: March 27, 2026</p>
          
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using pycrust.io ("the Service"), you accept and agree to be bound by 
              the terms and provision of this agreement. This Service is operated by Cube Solutions LLC.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              pycrust.io is a social media management platform that allows users to manage multiple 
              social media accounts, schedule content, and collaborate through workspaces. The Service currently 
              supports integration with multiple social platforms. Supported platforms and integrations may be added 
              or removed at any time.
            </p>
            <p><strong>Service Features:</strong> The Service enables users to upload content, organize content 
            in workspaces, schedule content for publication to connected platforms, collaborate through workspace invitations
            and permissions, and manage multiple social media accounts securely through OAuth authentication.</p>
            <p><strong>Service Modifications:</strong> We reserve the right to modify, remove, or discontinue platform 
            integrations, features, and usage limits at any time. We will provide reasonable notice of material changes 
            that affect your use of the Service.</p>
          </section>

          <section>
            <h2>3. Free Access, Trials, and Promotional Offers</h2>
            <p>
              We reserve the right to offer, modify, restrict, or revoke free access, trial periods, promotional features, 
              or any other offers at any time and at our sole discretion. Free access, trials, and promotional features are 
              provided at our discretion and may be terminated at any time with or without notice.
            </p>
            <p>
              <strong>No Obligations for Free Features:</strong> Regardless of any promotional offers, free trials, special 
              deals, or marketing materials, Cube Solutions LLC owes you nothing unless you have made a payment. Promotional 
              offers, free trials, and marketing materials do not create any legal obligations, guarantees, or entitlements. 
              We are not obligated to provide any free features, trials, or promotional benefits, and we may revoke or 
              modify them at any time at our sole discretion.
            </p>
            <p>
              <strong>Payment Required for Obligations:</strong> Our obligations to you only begin when you have made a 
              payment for a subscription. Until payment is received, we have no obligations to you, regardless of any 
              promotional offers, marketing materials, or other communications.
            </p>
          </section>

          <section>
            <h2>4. Subscription Plans</h2>
            <h3>4.1 Subscription Tiers</h3>
            <p>The Service is offered under the following subscription tiers:</p>
            <ul>
              <li>
                <strong>Free:</strong> 5 GB total storage; 1 workspace; 1 suite pool (connection limits per tier are enforced in the product as
                described at checkout and in-app); community support; up to 10 platform authentication attempts per day; scheduling
                and draft saves as implemented (subject to fair use and system limits).
              </li>
              <li>
                <strong>Starter (paid):</strong> 10 workspaces; suite pool of 10; 25 GB storage;
                email support; higher authentication limits than Free. Billed monthly (price as shown at checkout, currently
                marketed at $99/month unless otherwise stated).
              </li>
              <li>
                <strong>Growth (paid):</strong> 25 workspaces; suite pool of 25; 100 GB storage;
                priority email support. Billed monthly (price as shown at checkout, currently marketed at $199/month unless
                otherwise stated).
              </li>
              <li>
                <strong>Agency (paid):</strong> 50 workspaces; suite pool of 50; 300 GB storage;
                priority email support. Billed monthly (price as shown at checkout, currently marketed at $349/month
                unless otherwise stated).
              </li>
              <li>
                <strong>Enterprise (paid):</strong> High or unlimited caps on workspaces and suite pools
                as implemented; 1 TB or greater storage tier as configured; dedicated support and SLA options and optional white-label as
                contracted. Billed monthly (price as shown at checkout for your contract).
              </li>
            </ul>
            <p>
              <strong>Free trials:</strong> We may offer a free trial for eligible new paid subscriptions as shown at checkout (currently up
              to 30 days unless otherwise stated). Trial eligibility, length, and availability may change.
            </p>
            <p><strong>Tier Changes:</strong> We reserve the right to modify tier features, limits, and pricing at any time. 
            Changes to your current subscription will be communicated in advance when required, and you may cancel if you do not agree to the changes.</p>
          </section>

          <section>
            <h2>5. User Accounts and Roles</h2>
            <p>
              To use our Service, you must create an account using Google OAuth authentication. You are responsible for:
            </p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials and OAuth tokens</li>
              <li>All activities that occur under your account, including actions taken by managers you authorize</li>
              <li>Providing accurate and complete information</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
            
            <h3>5.1 Workspaces and Collaboration</h3>
            <p>The Service is organized around workspaces, which allow teams to collaborate on content:</p>
            <ul>
              <li><strong>Workspace Ownership:</strong> Each workspace has an owner who controls workspace settings, member invitations, and permissions</li>
              <li><strong>Workspace Members:</strong> Users can be invited to workspaces with different permission levels (owner, member, invited)</li>
              <li><strong>Workspace Content:</strong> Each workspace has its own video library, connected accounts, and scheduled content</li>
              <li><strong>Account Ownership:</strong> Connected platform accounts are owned by the user who connects them, regardless of which workspace they are used in</li>
            </ul>

            <h3>5.2 Workspace Permissions</h3>
            <p><strong>CRITICAL CONSENT FOR WORKSPACE MEMBERS:</strong> By accepting a workspace invitation, you explicitly agree and consent that:</p>
            <ul>
              <li>Workspace owners and members (depending on permissions) will have access to the workspace's content library, uploaded videos, and scheduling information</li>
              <li>Workspace members with scheduling permissions can schedule and publish content to connected platform accounts within that workspace</li>
              <li>Workspace members can modify, edit, and manage scheduled content for platforms connected to the workspace</li>
              <li>You are responsible for all content published by workspace members on your connected accounts</li>
              <li>You can leave a workspace or revoke member access at any time through workspace settings</li>
            </ul>
            <p>You acknowledge that sharing workspace access gives members control over workspace content and connected accounts. You agree to hold Cube Solutions LLC harmless for any actions taken by authorized workspace members.</p>
          </section>

          <section>
            <h2>6. Platform Integrations</h2>
            <p><strong>Supported Platforms:</strong> We support multiple social platforms for scheduling and publishing (e.g. YouTube, Instagram, TikTok, LinkedIn, Facebook, X, Reddit). The list may be updated over time.</p>
            <p><strong>Platform Availability:</strong> Platform integrations may be modified, restricted, or discontinued at any time. We are not responsible for changes to third-party platform APIs, terms of service, or availability.</p>
            <p><strong>Third-Party Terms:</strong> By connecting your social media accounts, you agree to comply with the terms of service of those platforms. You are responsible for ensuring your content complies with platform policies.</p>
            <p><strong>OAuth Permissions:</strong> Connecting a platform account requires you to grant OAuth permissions. You can revoke these permissions at any time by disconnecting the platform account in your settings.</p>
            <p><strong>Account Limits:</strong> Your subscription tier determines your suite pool, how many platform accounts you can connect within that pool, how many workspaces you may use, and storage, as described in Section 4 (Free, Starter, Growth, Agency, and Enterprise).</p>
          </section>

          <section>
            <h2>7. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for illegal or unauthorized purposes</li>
              <li>Violate any laws, regulations, or third-party platform terms of service</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Upload, schedule, or publish content that is harmful, malicious, defamatory, or violates platform policies</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with the proper functioning of the Service or attempt to circumvent rate limits</li>
              <li>Use automated systems or scripts to abuse the Service</li>
              <li>Share or transfer your account credentials to unauthorized parties</li>
            </ul>
          </section>

          <section>
            <h2>8. Content and Intellectual Property</h2>
            <p>
              <strong>Your Content:</strong> You retain ownership of all content you upload to our Service. 
              By using our Service, you grant us a limited, non-exclusive license to use, store, process, 
              and transmit your content solely for the purpose of providing the Service to you, including 
              publishing your content to your connected social media platforms.
            </p>
            <p>
              <strong>Content Responsibility:</strong> You are solely responsible for all content you upload, 
              schedule, or publish through our Service. You represent and warrant that you have all necessary 
              rights, licenses, and permissions to use and publish your content, and that your content does 
              not violate any laws or third-party rights.
            </p>
            <p>
              <strong>Content Storage:</strong> Your video files are stored in Google Cloud Storage. We are 
              not responsible for any loss, corruption, or unauthorized access to your content, though we 
              implement industry-standard security measures.
            </p>
            <p>
              <strong>Prohibited Content:</strong> You agree not to upload, schedule, or publish content 
              that is illegal, harmful, threatening, abusive, defamatory, obscene, or violates any platform 
              terms of service or community guidelines.
            </p>
          </section>

          <section>
            <h2>9. Rate Limits and Storage</h2>
            <p>
              The Service enforces tier-based limits that match what the product and our systems apply (including storage, workspaces,
              connected accounts, and platform OAuth / authentication attempts):
            </p>
            <ul>
              <li>
                <strong>Free:</strong> 5 GB total storage; 1 workspace; 1 suite pool; 10 platform
                authentication attempts per day; scheduling and draft saves as configured (see Section 4).
              </li>
              <li>
                <strong>Starter:</strong> 10 workspaces; suite pool of 10; 25 GB storage; daily authentication
                limits as implemented in the product.
              </li>
              <li>
                <strong>Growth:</strong> 25 workspaces; suite pool of 25; 100 GB storage; daily authentication limits
                as implemented.
              </li>
              <li>
                <strong>Agency:</strong> 50 workspaces; suite pool of 50; 300 GB storage; daily authentication limits
                as implemented.
              </li>
              <li>
                <strong>Enterprise:</strong> Higher or unlimited caps as configured for your subscription; storage at 1 TB or greater tier as
                implemented; authentication limits as configured.
              </li>
              <li><strong>Daily limits:</strong> Counts such as authentication attempts reset on a daily basis as implemented in the Service. Storage limits apply to your content across workspaces.</li>
            </ul>
            <p>We reserve the right to modify, restrict, or enforce additional limits on usage, storage, or access. Changes to limits will be communicated in advance when possible.</p>
          </section>

          <section>
            <h2>10. Privacy</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy, which also governs your 
              use of the Service, to understand how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2>11. Service Availability</h2>
            <p>
              <strong>Service Availability:</strong> We strive to provide reliable service but do not guarantee 
              uninterrupted access or availability. The Service may be temporarily unavailable due to:
            </p>
            <ul>
              <li>Maintenance, updates, or technical issues</li>
              <li>Third-party platform API changes or disruptions</li>
              <li>Service modifications or feature changes</li>
              <li>Unforeseen technical problems</li>
            </ul>
            <p>
              <strong>Background Processing:</strong> Some operations, such as video metadata extraction and 
              thumbnail generation, are processed asynchronously. We do not guarantee processing times or 
              completion of background tasks.
            </p>
            <p>We are not liable for any loss or damage resulting from Service unavailability or interruptions. 
            In the event of extended downtime, we may provide service credits or refunds at our discretion.</p>
          </section>

          <section>
            <h2>12. Account Termination and Data Deletion</h2>
            <p>
              <strong>Your Right to Terminate:</strong> You may delete your account at any time through 
              your account settings. Upon account deletion:
            </p>
            <ul>
              <li>Your personal information, uploaded content, and associated data will be promptly deleted</li>
              <li>Your connected platform accounts will be disconnected</li>
              <li>All manager-client relationships will be terminated</li>
              <li>Data deletion is typically completed within a short timeframe after your request</li>
            </ul>
            <p>
              <strong>Our Right to Terminate:</strong> We may terminate or suspend your account at any time 
              for violation of these terms, abuse of the Service, or for any other reason at our sole 
              discretion. We are not required to provide notice or explanation for account termination.
            </p>
            <p>
              <strong>Effect of Termination:</strong> Upon termination, your right to use the Service will 
              immediately cease. We may delete your account and data without liability to you.
            </p>
          </section>

          <section>
            <h2>13. Payment Processing and Billing</h2>
            <p>
              The Service uses Stripe for payment processing. By subscribing to a paid plan (Starter, Growth, Agency, Enterprise, or any future paid tier we offer), you agree to the following:
            </p>
            <ul>
              <li><strong>Payment Processing:</strong> Payment processing is handled by Stripe, subject to Stripe's terms of service and privacy policy</li>
              <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly. Your subscription will automatically renew each month unless you cancel</li>
              <li><strong>Payment Method:</strong> You must provide a valid payment method. You authorize us to charge your payment method for all subscription fees</li>
              <li><strong>Price Changes:</strong> We reserve the right to modify subscription prices. Price changes will be communicated at least 30 days in advance. If you do not agree to the new price, you may cancel your subscription</li>
              <li><strong>Failed Payments:</strong> If payment fails, we may suspend or cancel your subscription. You are responsible for maintaining a valid payment method</li>
              <li><strong>Refunds:</strong> Refund policies are outlined in our Refund Policy</li>
              <li><strong>Subscription Management:</strong> You can manage your subscription, update payment methods, and cancel through the Stripe Customer Portal or by contacting us</li>
            </ul>
          </section>

          <section>
            <h2>14. Disclaimers</h2>
            <p>
              <strong>SERVICE DISCLAIMER:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" 
              WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING 
              BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND 
              UNINTERRUPTED OR ERROR-FREE OPERATION.
            </p>
            <p>
              <strong>Platform Integration Disclaimer:</strong> We are not responsible for:
            </p>
            <ul>
              <li>Changes to third-party platform APIs, terms of service, or policies</li>
              <li>Content published to third-party platforms through our Service</li>
              <li>Platform-specific restrictions, limitations, or account suspensions</li>
              <li>Loss of access to platform accounts due to platform policy violations</li>
            </ul>
            <p>
              <strong>Content Disclaimer:</strong> We are not responsible for the accuracy, legality, or 
              appropriateness of content you upload, schedule, or publish through our Service.
            </p>
          </section>

          <section>
            <h2>15. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CUBE SOLUTIONS LLC SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>LOSS OF DATA, CONTENT, OR REVENUE</li>
              <li>LOSS OF ACCESS TO PLATFORM ACCOUNTS OR CONTENT PUBLISHED TO THIRD-PARTY PLATFORMS</li>
              <li>SERVICE INTERRUPTIONS, DOWNTIME, OR UNAVAILABILITY</li>
              <li>ACTIONS TAKEN BY AUTHORIZED MANAGERS ON YOUR BEHALF</li>
              <li>CHANGES TO FEATURES, PLATFORM INTEGRATIONS, OR SERVICE FUNCTIONALITY</li>
            </ul>
            <p>
              OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED 
              THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $49 IF YOU HAVE NOT 
              PAID ANY AMOUNTS (SUCH AS DURING A FREE TRIAL).
            </p>
          </section>

          <section>
            <h2>16. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms of Service at any time. We will make reasonable 
              efforts to notify users of material changes through the Service or by email at least 30 days 
              in advance when possible, but you are responsible for reviewing these terms periodically.
            </p>
            <p>
              Your continued use of the Service after any changes constitutes acceptance of the modified 
              terms. If you do not agree to the modified terms, you must stop using the Service and 
              delete your account.
            </p>
          </section>

          <section>
            <h2>17. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms of Service shall be governed by and construed in accordance with the laws of 
              the jurisdiction in which Cube Solutions LLC operates, without regard to its conflict of law 
              provisions.
            </p>
            <p>
              Any disputes arising from or related to these terms or the Service shall be resolved through 
              binding arbitration or in the appropriate courts, as applicable.
            </p>
          </section>

          <section>
            <h2>18. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
            </p>
            <p>
              Email: contact@pycrust.io<br />
              Company: Cube Solutions LLC
            </p>
          </section>

          <section className="legal-page-footer-note">
            <p>&copy; 2025 pycrust.io. All rights reserved by Cube Solutions LLC.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
